import { useState } from 'react';
import checkSvg from 'assets/icons/check.svg';
import copySvg from 'assets/icons/copy.svg';
import Button from 'lib/Button';
import ButtonProps from 'lib/Button/Button.types';
import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';
import { copyToClipboard } from 'utils/copyToClipboard';

type Props = Pick<ButtonProps, 'label' | 'color' | 'isDisabled'> & {
  textToCopy: string;
  svgClassnames?: string;
  buttonClassnames?: string;
  dataTestId?: string;
};

const CopyToClipboardButton: React.FC<Props> = ({
  label,
  color,
  isDisabled,
  textToCopy,
  svgClassnames,
  buttonClassnames,
  dataTestId,
}) => {
  const [wasCopied, setWasCopied] = useState(false);
  const [copyResult, setCopyResult] = useState<string | null>(null);

  const onClick = async (): Promise<void> => {
    const copyToClipboardResult = await copyToClipboard(textToCopy);
    setCopyResult(copyToClipboardResult);

    if (!copyToClipboardResult && !wasCopied) setWasCopied(true);
  };

  const onMouseLeave = (): void => {
    if (wasCopied) {
      setWasCopied(false);
      setCopyResult(null);
    }
  };

  const getTooltipMessage = (): string | undefined => {
    if (copyResult) return copyResult;
    if (wasCopied) return 'Copied to clipboard';

    return undefined;
  };

  const tooltipMessage = getTooltipMessage();

  return (
    <Tooltip
      contentSize={TooltipSize.MAX}
      direction={TooltipDirection.BOTTOM}
      tooltip={tooltipMessage}
      isOpen={!!tooltipMessage}
      delay={false}
    >
      <Button
        dataTestId={dataTestId}
        color={color}
        svg={wasCopied ? checkSvg : copySvg}
        label={label}
        classNames={buttonClassnames}
        svgClassnames={svgClassnames}
        isDisabled={isDisabled}
        onClick={onClick}
        onMouseLeave={onMouseLeave}
      />
    </Tooltip>
  );
};

export default CopyToClipboardButton;
